import { saveAs } from 'file-saver';
import { WorkshopV2 } from '../../../interfaces/workshop.interface';
import { Participant } from '../../../interfaces/reservation.interface';
import { reservationPlaces } from '../../../utils/reservation';

/** Séparateur point-virgule : c'est ce qu'attend Excel en locale française. */
const SEPARATOR = ';';

/** Byte Order Mark : sans lui, Excel casse les accents à l'ouverture du CSV. */
const BOM = String.fromCharCode(0xfeff);

/** Marge confortable sous la limite de 255 caractères des noms de fichiers. */
const MAX_SLUG_LENGTH = 60;

/** Bornes des « combining diacritical marks » laissés par normalize('NFD'). */
const DIACRITIC_START = 768;
const DIACRITIC_END = 879;

/** Caractères par lesquels Excel reconnaît une formule. */
const FORMULA_STARTERS = ['=', '+', '-', '@', '\t', '\r'];

/**
 * Les noms et emails viennent du site vitrine : ce sont des saisies libres.
 * Un tableur exécute une cellule commençant par « = » ; on la préfixe d'une
 * apostrophe pour qu'Excel la traite comme du texte.
 */
const escapeCell = (value: string | number) => {
  const text = String(value ?? '');
  const safe = FORMULA_STARTERS.some((c) => text.startsWith(c))
    ? `'${text}`
    : text;
  return `"${safe.replace(/"/g, '""')}"`;
};

const toCsv = (rows: Array<Array<string | number>>) =>
  rows.map((row) => row.map(escapeCell).join(SEPARATOR)).join('\r\n');

const download = (csv: string, filename: string) =>
  saveAs(new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' }), filename);

const slugify = (text: string) => {
  const withoutAccents = text
    .normalize('NFD')
    .split('')
    .filter((char) => {
      const code = char.charCodeAt(0);
      return code < DIACRITIC_START || code > DIACRITIC_END;
    })
    .join('');

  return (
    withoutAccents
      .replace(/[^a-zA-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase()
      // Un titre à rallonge dépasserait la limite de longueur des noms de
      // fichiers ; on tronque avant que le navigateur ne s'en charge mal.
      .slice(0, MAX_SLUG_LENGTH)
      .replace(/-+$/g, '') || 'atelier'
  );
};

/** Date au format ISO court (2026-07-18), pour les noms de fichiers. */
const isoDay = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`;

const frenchDateTime = (date: Date) =>
  date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const typeLabel = (participant: Participant) =>
  participant.type === 'duo' ? 'Duo' : 'Solo';

/** Exporte les participants d'un seul atelier. */
export const exportWorkshopParticipants = (
  workshop: WorkshopV2,
  participants: Participant[]
) => {
  const day = workshop.date.toDate();
  const rows: Array<Array<string | number>> = [
    ['Nom', 'Email', 'Téléphone', 'Type', 'Places'],
    ...participants.map((p) => [
      p.displayName,
      p.email,
      p.phone,
      typeLabel(p),
      reservationPlaces(p),
    ]),
  ];

  download(
    toCsv(rows),
    `participants-${slugify(workshop.title)}-${isoDay(day)}.csv`
  );
};

/** Exporte la liste courante : une ligne par participant, atelier en colonnes. */
export const exportHistory = (
  workshops: WorkshopV2[],
  participantsByWorkshop: Record<string, Participant[]>
) => {
  const rows: Array<Array<string | number>> = [
    ['Atelier', 'Date', 'Lieu', 'Nom', 'Email', 'Téléphone', 'Type', 'Places'],
  ];

  workshops.forEach((workshop) => {
    const day = frenchDateTime(workshop.date.toDate());
    const participants = participantsByWorkshop[workshop.id] || [];

    if (participants.length === 0) {
      // On garde une trace des ateliers sans réservation
      rows.push([workshop.title, day, workshop.location, '—', '', '', '', 0]);
      return;
    }

    participants.forEach((p) => {
      rows.push([
        workshop.title,
        day,
        workshop.location,
        p.displayName,
        p.email,
        p.phone,
        typeLabel(p),
        reservationPlaces(p),
      ]);
    });
  });

  download(toCsv(rows), `historique-ateliers-${isoDay(new Date())}.csv`);
};
